import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Flame, CheckCircle2 } from "lucide-react";
import axios from "axios";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { toast } from "sonner";
import { useLang } from "../context/LanguageContext";
import { RevealLine, RichLine } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const buildMonth = (base) => {
  const start = base.startOf("month");
  const days = [];
  const startWeekday = (start.day() + 6) % 7;
  for (let i = 0; i < startWeekday; i++) days.push(null);
  for (let d = 1; d <= base.daysInMonth(); d++) days.push(start.date(d));
  return days;
};

const inputCls =
  "rounded-lg bg-white/[0.06] border border-white/15 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-[#ff6b00] focus:bg-white/[0.09] focus:outline-none transition-colors";

export const Booking = () => {
  const { t, lang } = useLang();
  const B = t.booking;
  const today = dayjs();
  const [month, setMonth] = useState(today.startOf("month"));
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const minMonth = today.startOf("month");
  const maxMonth = today.add(2, "month").startOf("month");
  const activeStep = selectedSlot ? 2 : selectedDate ? 1 : 0;

  useEffect(() => {
    if (!selectedDate) return;
    setLoadingSlots(true);
    setSelectedSlot(null);
    axios
      .get(`${API}/availability`, { params: { date: selectedDate.format("YYYY-MM-DD") } })
      .then((res) => setSlots(res.data.slots))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [selectedDate]);

  const dayState = (d) => {
    if (!d) return "empty";
    if (d.isBefore(today, "day")) return "past";
    const wd = (d.day() + 6) % 7;
    if (wd === 6) return "closed";
    if (wd <= 3) return "full";
    return "open";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.service) {
      toast.error(B.errRequired);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/appointments`, {
        ...form,
        date: selectedDate.format("YYYY-MM-DD"),
        time_slot: selectedSlot,
        language: lang,
      });
      setSuccess(true);
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error(B.errSlot);
        setSelectedSlot(null);
        const res = await axios.get(`${API}/availability`, { params: { date: selectedDate.format("YYYY-MM-DD") } });
        setSlots(res.data.slots);
      } else {
        toast.error(err.response?.data?.detail || "Error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setSuccess(false);
    setSelectedDate(null);
    setSelectedSlot(null);
    setForm({ name: "", phone: "", email: "", service: "", message: "" });
  };

  const calDates = () => {
    const [time, mer] = selectedSlot.split(" ");
    let [h, m] = time.split(":").map(Number);
    if (mer === "PM" && h !== 12) h += 12;
    if (mer === "AM" && h === 12) h = 0;
    const start = selectedDate.hour(h).minute(m).second(0);
    return { start: start.format("YYYYMMDDTHHmmss"), end: start.add(1, "hour").format("YYYYMMDDTHHmmss") };
  };

  const googleCalUrl = () => {
    const { start, end } = calDates();
    const p = new URLSearchParams({
      action: "TEMPLATE",
      text: `Emberline Chimney Co. — ${form.service}`,
      dates: `${start}/${end}`,
      ctz: "America/New_York",
      details: B.successText,
      location: "12501 Lorain Ave, Cleveland, OH 44111",
    });
    return `https://calendar.google.com/calendar/render?${p.toString()}`;
  };

  const downloadIcs = () => {
    const { start, end } = calDates();
    const ics = [
      "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Emberline Chimney Co.//EN", "BEGIN:VEVENT",
      `UID:emberline-${start}@emberlinechimney.com`,
      `DTSTART;TZID=America/New_York:${start}`,
      `DTEND;TZID=America/New_York:${end}`,
      `SUMMARY:Emberline Chimney Co. — ${form.service}`,
      "LOCATION:12501 Lorain Ave\\, Cleveland\\, OH 44111",
      `DESCRIPTION:${B.successText}`,
      "END:VEVENT", "END:VCALENDAR",
    ].join("\r\n");
    const url = URL.createObjectURL(new Blob([ics], { type: "text/calendar" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "emberline-cita.ics";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section id="booking" data-testid="booking-section" className="relative py-24 sm:py-36 bg-neutral-950 overflow-hidden">
      <img src="/videos/embers.jpg" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-[0.14]" />
      <div className="absolute inset-0 bg-gradient-to-b from-neutral-950 via-neutral-950/60 to-neutral-950" />
      <div className="absolute -top-40 -left-40 w-[34rem] h-[34rem] rounded-full bg-[#ff6b00]/15 blur-[140px] pointer-events-none" />
      <div className="absolute -bottom-40 -right-40 w-[34rem] h-[34rem] rounded-full bg-[#ffd700]/10 blur-[140px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <RevealLine>
              <span className="font-tech text-[11px] tracking-[0.4em] uppercase text-white/40">— {B.kicker}</span>
            </RevealLine>
            <h2 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-white">
              {B.lines.map((line, i) => (
                <RevealLine key={i} delay={0.1 + i * 0.12}>
                  <RichLine segments={line} />
                </RevealLine>
              ))}
            </h2>
          </div>
          <span
            data-testid="booking-demand-badge"
            className="inline-flex items-center gap-2 rounded-full border border-[#ff6b00]/50 bg-[#ff6b00]/15 backdrop-blur px-4 py-2 font-tech text-[11px] tracking-widest uppercase text-[#ffb066] pulse-glow"
          >
            <Flame size={13} /> {B.demandBadge}
          </span>
        </div>
        <p className="mt-5 text-sm text-white/50 max-w-lg">{B.sub}</p>

        <div data-testid="booking-stepper" className="mt-12 max-w-3xl">
          <div className="flex justify-between">
            {[B.step1, B.step2, B.step3].map((label, i) => (
              <div key={i} className={`flex items-center gap-3 transition-opacity duration-500 ${i <= activeStep ? "opacity-100" : "opacity-40"}`}>
                <span
                  className={`w-9 h-9 rounded-full flex items-center justify-center font-tech text-xs transition-all duration-500 ${
                    i < activeStep
                      ? "bg-gradient-to-br from-[#ff6b00] to-[#ffd700] text-neutral-950 font-bold"
                      : i === activeStep
                      ? "border border-[#ff6b00] text-[#ffb066] shadow-[0_0_18px_rgba(255,107,0,0.35)]"
                      : "border border-white/20 text-white/50"
                  }`}
                >
                  0{i + 1}
                </span>
                <span className="hidden sm:block font-tech text-[10px] tracking-[0.25em] uppercase text-white/60">{label}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 h-[2px] bg-white/10 rounded-full overflow-hidden">
            <motion.span
              className="block h-full bg-gradient-to-r from-[#ff6b00] to-[#ffd700]"
              animate={{ width: `${(activeStep / 2) * 100}%` }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            />
          </div>
        </div>

        <AnimatePresence mode="wait">
          {success ? (
            <motion.div
              key="success"
              data-testid="booking-success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="mt-14 rounded-2xl border border-white/10 bg-white/[0.05] backdrop-blur-xl p-12 sm:p-16 text-center max-w-xl mx-auto shadow-[0_0_60px_rgba(255,107,0,0.12)]"
            >
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", delay: 0.2 }}>
                <CheckCircle2 size={64} className="mx-auto text-[#ff6b00] drop-shadow-[0_0_18px_rgba(255,107,0,0.5)]" />
              </motion.div>
              <h3 className="font-display text-3xl mt-6 text-white">{B.successTitle}</h3>
              <p className="mt-3 text-sm text-[#ffb066] font-tech tracking-wide">
                {selectedDate?.locale(lang).format("dddd, MMMM D")} — {selectedSlot}
              </p>
              <p className="mt-2 text-sm text-white/50">{B.successText}</p>
              <p className="mt-8 font-tech text-[10px] tracking-[0.3em] uppercase text-white/40">{B.addCal}</p>
              <div className="mt-3 flex flex-wrap justify-center gap-3">
                <a
                  data-testid="cal-google-btn"
                  href={googleCalUrl()}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-full px-5 py-2.5 font-tech text-[11px] tracking-widest uppercase text-white border border-[#ff6b00]/60 bg-[#ff6b00]/15 hover:bg-[#ff6b00]/30 transition-colors"
                >
                  Google Calendar
                </a>
                <button
                  data-testid="cal-ics-btn"
                  onClick={downloadIcs}
                  className="rounded-full px-5 py-2.5 font-tech text-[11px] tracking-widest uppercase text-white border border-white/30 hover:border-white/60 transition-colors"
                >
                  Apple / Outlook (.ics)
                </button>
              </div>
              <button
                data-testid="book-another-btn"
                onClick={reset}
                className="mt-8 rounded-full px-6 py-3 font-tech text-xs tracking-widest uppercase text-white border border-white/25 hover:border-[#ff6b00] hover:text-[#ffb066] transition-colors"
              >
                {B.bookAnother}
              </button>
            </motion.div>
          ) : (
            <motion.div key="flow" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
              <div className="lg:col-span-5 rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-6 sm:p-8">
                <div className="flex items-center justify-between mb-6">
                  <p className="font-tech text-xs tracking-[0.25em] uppercase text-white/50">{B.step1}</p>
                  <div className="flex items-center gap-2">
                    <button
                      data-testid="calendar-prev-month"
                      disabled={month.isSame(minMonth, "month")}
                      onClick={() => setMonth(month.subtract(1, "month"))}
                      className="p-1.5 rounded-full border border-white/20 text-white/60 hover:text-white hover:border-white/50 disabled:opacity-25 transition-colors"
                    >
                      <ChevronLeft size={15} />
                    </button>
                    <span className="font-tech text-sm text-white w-32 text-center">
                      {B.months[month.month()]} {month.year()}
                    </span>
                    <button
                      data-testid="calendar-next-month"
                      disabled={month.isSame(maxMonth, "month")}
                      onClick={() => setMonth(month.add(1, "month"))}
                      className="p-1.5 rounded-full border border-white/20 text-white/60 hover:text-white hover:border-white/50 disabled:opacity-25 transition-colors"
                    >
                      <ChevronRight size={15} />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-7 gap-1.5 mb-2">
                  {B.days.map((d) => (
                    <span key={d} className="text-center font-tech text-[10px] tracking-widest uppercase text-white/30 py-1">{d}</span>
                  ))}
                </div>
                <div data-testid="booking-calendar" className="grid grid-cols-7 gap-1.5">
                  {buildMonth(month).map((d, i) => {
                    const state = dayState(d);
                    const isSelected = d && selectedDate && d.isSame(selectedDate, "day");
                    return (
                      <button
                        key={i}
                        data-testid={d ? `calendar-day-${d.format("YYYY-MM-DD")}` : undefined}
                        disabled={state !== "open"}
                        onClick={() => setSelectedDate(d)}
                        className={`aspect-square rounded-lg flex flex-col items-center justify-center text-xs font-tech transition-all duration-200
                          ${state === "empty" ? "invisible" : ""}
                          ${state === "past" ? "text-white/15" : ""}
                          ${state === "closed" ? "text-white/15 bg-white/[0.02]" : ""}
                          ${state === "full" ? "text-white/30 bg-white/[0.04] cursor-not-allowed" : ""}
                          ${state === "open" && !isSelected ? "text-[#ffb066] border border-[#ff6b00]/60 bg-[#ff6b00]/10 hover:bg-[#ff6b00]/25 hover:shadow-[0_0_16px_rgba(255,107,0,0.3)]" : ""}
                          ${isSelected ? "bg-gradient-to-br from-[#ff6b00] to-[#ff9500] text-white font-bold shadow-[0_0_24px_rgba(255,107,0,0.5)] scale-[1.04]" : ""}`}
                      >
                        <span>{d ? d.date() : ""}</span>
                        {state === "full" && <span className="text-[7px] tracking-wider text-red-400/70 uppercase">{B.full}</span>}
                        {state === "open" && !isSelected && <span className="text-[7px] tracking-wider uppercase">{B.available}</span>}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-5 flex flex-wrap gap-4 text-[10px] font-tech tracking-widest uppercase text-white/35">
                  <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded bg-white/10" /> {B.legend_full}</span>
                  <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded bg-[#ff6b00]/40 border border-[#ff6b00]" /> {B.legend_open}</span>
                </div>
              </div>

              <div className="lg:col-span-7 flex flex-col gap-6">
                <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-6 sm:p-8">
                  <p className="font-tech text-xs tracking-[0.25em] uppercase text-white/50 mb-5">{B.step2}</p>
                  {!selectedDate ? (
                    <p className="text-sm text-white/30 italic font-display">— {B.step1}</p>
                  ) : loadingSlots ? (
                    <div className="flex flex-wrap gap-3">{Array.from({ length: 6 }).map((_, i) => <span key={i} className="h-10 w-24 rounded-full bg-white/[0.06] animate-pulse" />)}</div>
                  ) : (
                    <div data-testid="time-slots" className="flex flex-wrap gap-3">
                      {slots.map((s, si) => (
                        <motion.button
                          key={s.time}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: si * 0.05 }}
                          data-testid={`time-slot-${s.time.replace(/[\s:]/g, "-")}`}
                          disabled={!s.available}
                          onClick={() => setSelectedSlot(s.time)}
                          className={`rounded-full px-5 py-2.5 font-tech text-xs tracking-widest transition-all duration-200
                            ${!s.available ? "border border-white/10 text-white/25 line-through cursor-not-allowed" : ""}
                            ${s.available && selectedSlot !== s.time ? "border border-[#ff6b00]/50 text-[#ffb066] hover:bg-[#ff6b00]/15 hover:shadow-[0_0_14px_rgba(255,107,0,0.25)]" : ""}
                            ${selectedSlot === s.time ? "bg-gradient-to-r from-[#ff6b00] to-[#ff9500] text-white border border-transparent shadow-[0_0_22px_rgba(255,107,0,0.45)]" : ""}`}
                        >
                          {s.time} {!s.available && `· ${B.taken}`}
                        </motion.button>
                      ))}
                    </div>
                  )}
                </div>

                {!selectedSlot && (
                  <div className="hidden lg:block relative flex-1 min-h-[220px] rounded-2xl overflow-hidden border border-white/10">
                    <img src="/videos/fireplace.jpg" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-35" />
                    <div className="absolute inset-0 bg-gradient-to-t from-neutral-950/85 via-transparent to-transparent" />
                    <p className="absolute bottom-5 left-6 font-display italic text-2xl text-white/85">{B.hint}</p>
                  </div>
                )}

                <AnimatePresence>
                  {selectedSlot && (
                    <motion.form
                      data-testid="booking-form"
                      initial={{ opacity: 0, y: 24 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      onSubmit={submit}
                      className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-6 sm:p-8"
                    >
                      <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-dashed border-white/15 pb-5 mb-6">
                        <p className="font-tech text-xs tracking-[0.25em] uppercase text-white/50">{B.step3}</p>
                        <p data-testid="booking-summary" className="font-display italic text-xl sm:text-2xl text-white">
                          {selectedDate?.locale(lang).format("ddd, MMM D")} — <span className="text-gradient-fire not-italic font-tech text-lg tracking-wide">{selectedSlot}</span>
                        </p>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <input data-testid="booking-input-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={B.name} className={inputCls} />
                        <input data-testid="booking-input-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={B.phone} className={inputCls} />
                        <input data-testid="booking-input-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={B.email} className={inputCls} />
                        <select
                          data-testid="booking-select-service"
                          value={form.service}
                          onChange={(e) => setForm({ ...form, service: e.target.value })}
                          className={`${inputCls} ${form.service ? "text-white" : "text-white/30"} [&>option]:text-neutral-900`}
                        >
                          <option value="">{B.selectServicePlaceholder}</option>
                          {B.services.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <textarea data-testid="booking-input-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={B.message} rows={3} className={`sm:col-span-2 resize-none ${inputCls}`} />
                      </div>
                      <button
                        data-testid="booking-submit-btn"
                        type="submit"
                        disabled={submitting}
                        className="btn-fire mt-6 w-full sm:w-auto rounded-full px-10 py-4 font-tech text-sm font-semibold tracking-widest uppercase text-white disabled:opacity-60"
                      >
                        {submitting ? B.submitting : B.submit}
                      </button>
                    </motion.form>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};
